// frontend/src/services/api/modules/documentos.js
/**
 * API functions for document management (Premium objects only)
 */
import { API_URL } from '../config';
import { getAuthHeaders } from '../utils/fetchWrapper';

/**
 * Listar todos los documentos de un objeto
 * @param {string} objetoId - ID del objeto
 * @returns {Promise<{documentos: Array, espacio: Object}|{error: string}>}
 */
export const listarDocumentos = async (objetoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/objetos/${objetoId}/documentos`, {
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al listar documentos' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en listarDocumentos:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Obtener información de espacio usado
 * @param {string} objetoId - ID del objeto
 * @returns {Promise<Object>}
 */
export const obtenerEspacio = async (objetoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/objetos/${objetoId}/documentos/espacio`, {
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al obtener espacio' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en obtenerEspacio:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Obtener un documento con sus links de compartir
 * @param {string} objetoId - ID del objeto
 * @param {string} documentoId - ID del documento
 * @returns {Promise<Object>}
 */
export const obtenerDocumento = async (objetoId, documentoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/objetos/${objetoId}/documentos/${documentoId}`, {
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al obtener documento' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en obtenerDocumento:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Subir un nuevo documento
 * @param {string} objetoId - ID del objeto
 * @param {string} nombre - Nombre del documento
 * @param {File} archivo - Archivo a subir
 * @param {function} onProgress - Callback para progreso (opcional)
 * @returns {Promise<Object>}
 */
export const subirDocumento = async (objetoId, nombre, archivo, onProgress = null) => {
  try {
    const headers = await getAuthHeaders();
    // Remove Content-Type to let browser set multipart boundary
    delete headers['Content-Type'];

    const formData = new FormData();
    formData.append('nombre', nombre);
    formData.append('archivo', archivo);

    // Use XMLHttpRequest for progress tracking
    if (onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded / e.total) * 100);
            onProgress(percent);
          }
        });

        xhr.addEventListener('load', () => {
          try {
            const response = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(response);
            } else {
              resolve({ error: response.error || 'Error al subir documento' });
            }
          } catch (e) {
            resolve({ error: 'Error al procesar respuesta' });
          }
        });

        xhr.addEventListener('error', () => {
          resolve({ error: 'Error de conexión' });
        });

        xhr.open('POST', `${API_URL}/api/objetos/${objetoId}/documentos`);

        // Add auth headers
        Object.keys(headers).forEach(key => {
          xhr.setRequestHeader(key, headers[key]);
        });

        xhr.send(formData);
      });
    }

    // Simple fetch without progress
    const response = await fetch(`${API_URL}/api/objetos/${objetoId}/documentos`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || errorData.detalle || 'Error al subir documento' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en subirDocumento:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Regenerar tokens de un documento
 * @param {string} objetoId - ID del objeto
 * @param {string} documentoId - ID del documento
 * @param {string} tipo - 'consulta', 'descarga', o 'ambos'
 * @returns {Promise<Object>}
 */
export const regenerarToken = async (objetoId, documentoId, tipo = 'ambos') => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/objetos/${objetoId}/documentos/${documentoId}/regenerar-token`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ tipo })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al regenerar token' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en regenerarToken:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Eliminar un documento
 * @param {string} objetoId - ID del objeto
 * @param {string} documentoId - ID del documento
 * @returns {Promise<Object>}
 */
export const eliminarDocumento = async (objetoId, documentoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/objetos/${objetoId}/documentos/${documentoId}`,
      {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ confirmacion: 'borrar' })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al eliminar documento' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en eliminarDocumento:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Formatea el tamaño en bytes a un string legible
 * @param {number} bytes - Tamaño en bytes
 * @returns {string} Ej: "1.5 MB", "500 KB"
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Obtiene el icono según el tipo MIME
 * @param {string} mimeType - Tipo MIME del archivo
 * @returns {string} Clase de Bootstrap Icon
 */
export const getFileIcon = (mimeType) => {
  if (mimeType === 'application/pdf') return 'bi-file-earmark-pdf-fill text-danger';
  if (mimeType?.startsWith('image/')) return 'bi-file-earmark-image-fill text-primary';
  return 'bi-file-earmark-fill';
};

/**
 * Obtiene extensión legible según tipo MIME
 * @param {string} mimeType - Tipo MIME
 * @returns {string} Extensión (PDF, JPG, PNG)
 */
export const getFileExtension = (mimeType) => {
  const extensions = {
    'application/pdf': 'PDF',
    'image/jpeg': 'JPG',
    'image/png': 'PNG'
  };
  return extensions[mimeType] || 'FILE';
};

// =============================================
// CONTACTO Y RECORDATORIO
// =============================================

/**
 * Guardar o actualizar contacto de un documento
 * @param {string} objetoId - ID del objeto
 * @param {string} documentoId - ID del documento
 * @param {Object} contacto - Datos del contacto
 * @returns {Promise<Object>}
 */
export const guardarContacto = async (objetoId, documentoId, contacto) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/objetos/${objetoId}/documentos/${documentoId}/contacto`,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify(contacto)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al guardar contacto' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en guardarContacto:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Eliminar contacto de un documento
 * @param {string} objetoId - ID del objeto
 * @param {string} documentoId - ID del documento
 * @returns {Promise<Object>}
 */
export const eliminarContacto = async (objetoId, documentoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/objetos/${objetoId}/documentos/${documentoId}/contacto`,
      {
        method: 'DELETE',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al eliminar contacto' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en eliminarContacto:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Guardar o actualizar recordatorio de un documento
 * @param {string} objetoId - ID del objeto
 * @param {string} documentoId - ID del documento
 * @param {Object} recordatorio - Datos del recordatorio
 * @returns {Promise<Object>}
 */
export const guardarRecordatorio = async (objetoId, documentoId, recordatorio) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/objetos/${objetoId}/documentos/${documentoId}/recordatorio`,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify(recordatorio)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al guardar recordatorio' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en guardarRecordatorio:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Analizar documento con IA para extraer fechas de vencimiento
 * @param {string} objetoId - ID del objeto
 * @param {string} documentoId - ID del documento
 * @returns {Promise<Object>}
 */
export const analizarDocumentoRecordatorio = async (objetoId, documentoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/objetos/${objetoId}/documentos/${documentoId}/analizar-recordatorio`,
      {
        method: 'POST',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al analizar documento' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en analizarDocumentoRecordatorio:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Crear múltiples recordatorios desde análisis de documento
 * @param {string} objetoId - ID del objeto
 * @param {Object} datos - { recordatorios, documentoId, documentoNombre }
 * @returns {Promise<Object>}
 */
export const crearRecordatoriosLoteDocumento = async (objetoId, datos) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/objetos/${objetoId}/recordatorios-objetos/lote-documento`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(datos)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al crear recordatorios' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en crearRecordatoriosLoteDocumento:', error);
    return { error: 'Error de conexión' };
  }
};

// =============================================
// RECORDATORIOS DE OBJETOS (subcollection)
// =============================================

/**
 * Obtener todos los recordatorios de un objeto
 * @param {string} objetoId - ID del objeto
 * @returns {Promise<Object>}
 */
export const obtenerRecordatoriosObjeto = async (objetoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/objetos/${objetoId}/recordatorios-objetos`,
      { headers }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al obtener recordatorios' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en obtenerRecordatoriosObjeto:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Actualizar un recordatorio de objeto
 * @param {string} objetoId - ID del objeto
 * @param {string} recordatorioId - ID del recordatorio
 * @param {Object} datos - Campos a actualizar
 * @returns {Promise<Object>}
 */
export const actualizarRecordatorioObjeto = async (objetoId, recordatorioId, datos) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/objetos/${objetoId}/recordatorios-objetos/${recordatorioId}`,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify(datos)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al actualizar recordatorio' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en actualizarRecordatorioObjeto:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Eliminar un recordatorio de objeto
 * @param {string} objetoId - ID del objeto
 * @param {string} recordatorioId - ID del recordatorio
 * @returns {Promise<Object>}
 */
export const eliminarRecordatorioObjeto = async (objetoId, recordatorioId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/objetos/${objetoId}/recordatorios-objetos/${recordatorioId}`,
      {
        method: 'DELETE',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al eliminar recordatorio' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en eliminarRecordatorioObjeto:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Eliminar todos los recordatorios de un grupo (documento)
 * @param {string} objetoId - ID del objeto
 * @param {string} grupoId - ID del grupo de recordatorios
 * @returns {Promise<Object>}
 */
export const eliminarGrupoRecordatorios = async (objetoId, grupoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/objetos/${objetoId}/recordatorios-objetos/grupo/${grupoId}`,
      {
        method: 'DELETE',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al eliminar grupo de recordatorios' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en eliminarGrupoRecordatorios:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Descargar pase de Apple Wallet para un recordatorio de documento
 * @param {string} objetoId - ID del objeto
 * @param {string} docId - ID del documento
 * @param {string} recordatorioId - ID del recordatorio
 * @returns {Promise<Blob|Object>} - Blob del .pkpass o error
 */
export const descargarWalletPassDocumento = async (objetoId, docId, recordatorioId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/objetos/${objetoId}/documentos/${docId}/wallet-pass?recordatorioId=${recordatorioId}`,
      { headers }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al generar el pase' };
    }

    const blob = await response.blob();
    return { blob };
  } catch (error) {
    console.error('Error en descargarWalletPassDocumento:', error);
    return { error: 'Error de conexión' };
  }
};

// =============================================
// COMPARTIR RECORDATORIOS (.ics)
// =============================================

/**
 * Crear link para compartir recordatorios como .ics
 * @param {string} objetoId - ID del objeto
 * @param {string[]} recordatorioIds - IDs de los recordatorios a compartir
 * @returns {Promise<{token: string, shareUrl: string}|{error: string}>}
 */
export const compartirRecordatorios = async (objetoId, recordatorioIds) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/objetos/${objetoId}/recordatorios-compartir`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ recordatorioIds })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al compartir recordatorios' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en compartirRecordatorios:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Revocar un link de compartir recordatorios
 * @param {string} objetoId - ID del objeto
 * @param {string} token - Token del link a revocar
 * @returns {Promise<Object>}
 */
export const revocarLinkRecordatorios = async (objetoId, token) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/objetos/${objetoId}/recordatorios-compartir/${token}`,
      {
        method: 'DELETE',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al revocar link' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en revocarLinkRecordatorios:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Obtener recordatorios compartidos (público, sin auth)
 * @param {string} token - Token del link compartido
 * @returns {Promise<Object>}
 */
export const obtenerRecordatoriosCompartidos = async (token) => {
  try {
    const response = await fetch(
      `${API_URL}/api/recordatorios-compartidos/publico/${token}`
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Link no disponible', revocado: errorData.revocado };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en obtenerRecordatoriosCompartidos:', error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Eliminar recordatorio de un documento
 * @param {string} objetoId - ID del objeto
 * @param {string} documentoId - ID del documento
 * @returns {Promise<Object>}
 */
export const eliminarRecordatorio = async (objetoId, documentoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/api/objetos/${objetoId}/documentos/${documentoId}/recordatorio`,
      {
        method: 'DELETE',
        headers
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al eliminar recordatorio' };
    }

    return await response.json();
  } catch (error) {
    console.error('Error en eliminarRecordatorio:', error);
    return { error: 'Error de conexión' };
  }
};
